import { useCallback, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { PanelBase, PanelBaseType } from '@pages/NewQuote/PanelBase/PanelBase';
import { TypeAheadEntry } from '@pages/NewQuote/location/TypeAheadEntry';
import { ITogglePanel } from '@pages/NewQuote/TogglePanel';
import { useNewQuoteStore } from '@store/NewQuote';
import { useStore } from '@store/GlobalStore';
import { useInsuredLocations } from '@services/customer';
import { useSearchBrokerageOfferings } from '@services/brokerage';
import { AddressInput, EnteredAddress } from '@models/validation';
import { Customer } from '@models/customer';
import { areAddressesEqual } from '@utils/address';
import { ManualAddressEntry } from '@pages/QuoteDetails/components/ManualAddressEntry';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { ProductOfferingId } from '@models/newQuote';
import { states } from '@utils/states';

export interface ILocationPanel {
  hasIneligibilities: boolean | undefined;
}

export const LocationOpenPanel = ({ onClose, hasIneligibilities }: ITogglePanel & ILocationPanel) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const { location, namedInsured, scope, updateAddress } = useNewQuoteStore();
  const { isInternalUser } = useStore((state) => state.userAuth);
  const { isSpecialEvent, productOffering, effectiveDate, brokerageAgency } = scope;
  const customerId = namedInsured?.customer?.id;
  const { insuredLocation } = location;
  const { customer } = namedInsured;
  const { businessAddress } = customer as Customer;
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const { insuredLocations } = useInsuredLocations({
    sort: 'isPrimary',
    customerId,
  });

  const jurisdictionRequestBody = {
    brokerageId: brokerageAgency?.value,
    effectiveDate: effectiveDate ?? undefined,
    enabled: true,
    offeringId: productOffering?.value,
  };

  const { isLoading: isBrokerageOfferingsLoading, data: brokerageOfferings } =
    useSearchBrokerageOfferings(jurisdictionRequestBody);

  const availableJurisdictionsRaw: string[] = !isBrokerageOfferingsLoading
    ? (brokerageOfferings?.items.map((item) => item.jurisdiction.abbreviation) ?? [])
    : [];
  const shouldExcludeCA = !isInternalUser && productOffering?.value === ProductOfferingId.CondoOwnersAssociation;
  const availableJurisdictions: string[] = shouldExcludeCA
    ? availableJurisdictionsRaw.filter((abbr) => abbr.toUpperCase() !== 'CA')
    : availableJurisdictionsRaw;
  const sortedJurisdictions: string[] = Array.from(new Set(availableJurisdictions)).sort();
  // Internal users may proceed with a jurisdiction the offering isn't live in yet (LIO-28733) —
  // e.g. to create a shell quote pending the LSIC issuing-company override — so their manual-entry
  // state picker isn't limited to only the currently-available list the way a broker's is.
  const stateSelectionOptions = isInternalUser
    ? states.map(({ label, value }) => ({ label, value }))
    : sortedJurisdictions.map((stateCode: string) => ({
        label: stateCode.toUpperCase(),
        value: stateCode.toUpperCase(),
      }));

  const handleChangeAddress = useCallback(
    (addressToUpdate: EnteredAddress) => {
      const getExistingAddress = (value: EnteredAddress) =>
        insuredLocations?.items?.find((el: any) => {
          const { address } = el;
          const addressToCompare: EnteredAddress = {
            street1: address.street1,
            street2: address.street2,
            state: address.state,
            city: address.city,
            zipCode: address.zipCode,
          };
          return areAddressesEqual(addressToCompare, value);
        });
      const existingAddress = getExistingAddress(addressToUpdate);
      if (existingAddress && !isSpecialEvent) {
        const { address: a } = existingAddress;
        const newValue: EnteredAddress = {
          id: existingAddress.id,
          street1: a.street1,
          street2: a.street2,
          city: a.city,
          state: a.state,
          zipCode: a.zipCode,
        };

        updateAddress(newValue);
      } else {
        updateAddress({ ...addressToUpdate });
      }
    },
    [updateAddress, insuredLocations, isSpecialEvent],
  );

  useEffect(() => {
    if (businessAddress && !location.insuredLocation.state && !isSpecialEvent) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...address } = businessAddress;
      handleChangeAddress(address);
    }
  }, [businessAddress, location.insuredLocation.state, handleChangeAddress, isSpecialEvent]);

  // Internal users are never blocked by jurisdiction availability here (LIO-28733) — only
  // address completeness matters for them; the available-jurisdictions list still surfaces to
  // them informationally (see TypeAheadEntry's hover tooltip and shouldValidateJurisdiction below).
  const isAddressValid = isInternalUser || availableJurisdictions.includes(insuredLocation.state);

  const someAddressIncomplete =
    !insuredLocation.street1 || !insuredLocation.city || !insuredLocation.state || !insuredLocation.zipCode;
  const isDisabled = isLoading || someAddressIncomplete || !isAddressValid;

  const handleChangeAddressEntry = () => {
    setIsManualEntry((prevState) => !prevState);
  };

  const handleConfirm = () => {
    onClose();
  };

  return (
    <PanelBase
      name='Location'
      step='3 of 4'
      type={PanelBaseType.Open}
      confirmButtonProps={{
        children: hasIneligibilities ? 'Continue to Eligibility' : 'Continue',
        disabled: isDisabled,
        onClick: handleConfirm,
      }}
    >
      <Grid
        container
        rowSpacing='8px'
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '4px',
          paddingBottom: '20px',
        }}
      >
        <Typography variant='h4'>
          {isSpecialEvent ? 'Where is the event taking place?' : 'What location is being insured?'}
        </Typography>

        {isManualEntry ? (
          <>
            <Typography variant='bodySmall'>Enter your location manually</Typography>

            <Grid
              size={{ xs: 12 }}
              sx={{
                width: isMobileOrSmallScreen ? '100%' : '500px',
              }}
            >
              <ManualAddressEntry
                allowStateSelection
                availableStateSelections={stateSelectionOptions}
                address={location.insuredLocation as AddressInput}
                buttonText='Use this address'
                updateAddress={handleChangeAddress}
                handleSelectAddress={(address: EnteredAddress) => handleChangeAddress(address)}
                handleCancel={() => setIsManualEntry(false)}
              />
            </Grid>
          </>
        ) : (
          <TypeAheadEntry
            hoverText='The following states are available:'
            onChangeAddressEntry={handleChangeAddressEntry}
            setIsLoading={setIsLoading}
            stateList={sortedJurisdictions}
            updateAddress={handleChangeAddress}
            shouldValidateJurisdiction={!isInternalUser}
          />
        )}
      </Grid>
    </PanelBase>
  );
};
